
import { Button } from "@/components/ui/button";
import {
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface Order {
  id: string;
  status: string;
  total_price: number;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  payment_method: string;
  created_at: string;
}

interface OrderItem {
  id: string;
  product_id: string;
  quantity: number;
  price: number;
  notes: string;
  product: {
    name: string;
    description: string;
    image_url: string;
  };
}

interface OrderDetailsProps {
  order: Order;
  orderItems: OrderItem[];
  onUpdateStatus: (orderId: string, status: string) => void;
}

const OrderDetails = ({ order, orderItems, onUpdateStatus }: OrderDetailsProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-500";
      case "preparing":
        return "bg-blue-500";
      case "delivering":
        return "bg-purple-500";
      case "completed":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <>
      <SheetHeader className="mb-6">
        <SheetTitle className="text-foreground">
          Pedido #{order.id.slice(0, 8).toUpperCase()}
        </SheetTitle>
      </SheetHeader>

      <div className="space-y-6">
        <div className="space-y-1">
          <h3 className="text-sm text-muted-foreground">Status atual</h3>
          <div className="flex items-center">
            <div
              className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(order.status)}`}
            ></div>
            <span className="capitalize font-medium text-foreground">{order.status}</span>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm text-muted-foreground">Atualizar Status</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className={`${
                order.status === "pending"
                  ? "bg-yellow-500 text-black hover:bg-yellow-600 font-semibold"
                  : "border-border hover:bg-accent text-foreground"
              }`}
              onClick={() => onUpdateStatus(order.id, "pending")}
            >
              Recebido
            </Button>
            <Button
              variant="outline"
              className={`${
                order.status === "preparing"
                  ? "bg-blue-500 text-white hover:bg-blue-600 font-semibold"
                  : "border-border hover:bg-accent text-foreground"
              }`}
              onClick={() => onUpdateStatus(order.id, "preparing")}
            >
              Preparando
            </Button>
            <Button
              variant="outline"
              className={`${
                order.status === "delivering"
                  ? "bg-purple-500 text-white hover:bg-purple-600 font-semibold"
                  : "border-border hover:bg-accent text-foreground"
              }`}
              onClick={() => onUpdateStatus(order.id, "delivering")}
            >
              Em Entrega
            </Button>
            <Button
              variant="outline"
              className={`${
                order.status === "completed"
                  ? "bg-green-500 text-white hover:bg-green-600 font-semibold"
                  : "border-border hover:bg-accent text-foreground"
              }`}
              onClick={() => onUpdateStatus(order.id, "completed")}
            >
              Entregue
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="font-bold text-foreground">Informações do Cliente</h3>
          <div className="bg-muted/60 border border-border p-4 rounded-lg">
            <p className="font-semibold text-foreground">{order.customer_name}</p>
            <p className="text-sm text-muted-foreground">{order.customer_phone}</p>
            <p className="mt-2 text-sm text-foreground">{order.delivery_address}</p>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="font-bold text-foreground">Itens do Pedido</h3>
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {orderItems.map((item) => (
              <div
                key={item.id}
                className="flex gap-3 bg-muted/60 border border-border p-3 rounded-lg"
              >
                <div className="w-16 h-16 shrink-0 rounded overflow-hidden bg-background border border-border">
                  <img
                    src={item.product?.image_url || "/placeholder.svg"}
                    alt={item.product?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <p className="font-medium text-foreground">{item.product?.name}</p>
                    <p className="font-bold text-foreground">{item.quantity}x</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    R$ {item.price.toFixed(2)}
                  </p>
                  {item.notes && (
                    <p className="text-sm italic mt-1 text-muted-foreground">
                      "{item.notes}"
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-4 text-foreground">
          <div className="flex justify-between mb-2 text-sm">
            <span className="text-muted-foreground">Forma de pagamento:</span>
            <span className="capitalize font-medium">{order.payment_method}</span>
          </div>
          <div className="flex justify-between mb-2 text-sm">
            <span className="text-muted-foreground">Subtotal:</span>
            <span>R$ {order.total_price.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold text-xl text-krecao-yellow mt-4">
            <span>Total:</span>
            <span>R$ {order.total_price.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetails;
